import { Formik, FormikProps, useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import _ from 'lodash';
import {
  FormInputsContainer,
  ParcelParticipantDetailsContainer,
} from './parcel-participant-personal.styles';
import { useStore } from '../../../stores/store';
import { ServicesSubSectionHeader } from '../../services/services-subsection/services-subsection.styles';
import {
  prepareFormInputComponent,
  prepareInitialValues,
  prepareValidationSchema,
  prepareVisibilitySchema,
} from '../../../utils/elements-parser/elements-parser.utils';
import { toYYYYmmdd } from '../../../utils/generic/generic.utils';
import { ReactComponent as AddressBookIcon } from '../../../assets/address-book.svg';

interface GlobalStateMethods {
  setGlobalStateFormIsValid: (isValid: boolean) => void;
  setGlobalStateFormValues: (
    values: Record<string, string | null> | undefined
  ) => void;
}

interface ParcelSenderPersonalProps extends GlobalStateMethods {
  isReceiver?: boolean;
  innerRef?: React.Ref<FormikProps<Record<string, string | null>>>;
}

interface FormikEffectsProps extends GlobalStateMethods {
  triggerSubmit: { value: boolean };
  visibilitySchema: Record<string, boolean>;
  inputDateFormFieldNames: string[];
}

function FormikEffects({
  triggerSubmit,
  visibilitySchema,
  inputDateFormFieldNames,
  setGlobalStateFormValues,
  setGlobalStateFormIsValid,
}: FormikEffectsProps) {
  const { values, isValid, submitForm } =
    useFormikContext<Record<string, string | null>>();

  useEffect(() => {
    if (!triggerSubmit.value) return;

    submitForm();
  }, [triggerSubmit, submitForm]);

  useEffect(() => {
    setGlobalStateFormIsValid(isValid);
  }, [isValid, setGlobalStateFormIsValid]);

  useEffect(() => {
    const normalizedValues = _.cloneDeep(values);
    // find all values which belong to input-date type elements
    inputDateFormFieldNames.forEach((x) => {
      normalizedValues[x] = toYYYYmmdd(values[x]);
    });

    // remove those which are not visible currently
    Object.keys(normalizedValues).forEach((x) => {
      if (!visibilitySchema[x]) delete normalizedValues[x];
    });

    setGlobalStateFormValues(normalizedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, visibilitySchema, setGlobalStateFormValues]);

  return undefined;
}

const ParcelParticipantPersonalFormDynamic = ({
  isReceiver = false,
  innerRef,
  setGlobalStateFormIsValid,
  setGlobalStateFormValues,
}: ParcelSenderPersonalProps) => {
  const {
    navStore: { toggleSidebarByName },
    commonStore: { isGuestParcelCreation },
    parcelCreationStore: {
      isNextButtonClicked,
      navigation: { current },
    },
  } = useStore();

  const { t } = useTranslation();

  // Preparation of dynamic rendering based on the nav elements from the backend:
  // initValues, validationSchema and the inputElements

  const viewBlock = current?.elements.find(
    (elem) => elem.type === 'view_block'
  );

  const inputDateFormFieldNames = useMemo(
    () =>
      viewBlock?.elements
        ?.filter((x) => x.type === 'input-date' && x.behavior?.form_field)
        .map((x) => x.behavior!.form_field as string) ?? [],
    [viewBlock?.elements]
  );

  const initValues = useMemo(
    () => prepareInitialValues(viewBlock?.elements),
    [viewBlock?.elements]
  );

  const validationSchema = useMemo(
    () => prepareValidationSchema(viewBlock?.elements),
    [viewBlock?.elements]
  );

  const inputElements = useMemo(
    () =>
      viewBlock?.elements
        ?.map((element, i) => prepareFormInputComponent(element, i + 1))
        .filter((x) => !!x),
    [viewBlock?.elements]
  );

  return (
    <ParcelParticipantDetailsContainer>
      <div className='personal-form-header'>
        <ServicesSubSectionHeader>
          {isReceiver ? t('receiver_information') : t('user_data')}
        </ServicesSubSectionHeader>
        {isReceiver && !isGuestParcelCreation && (
          <button
            type='button'
            className='contacts-button'
            onClick={() => toggleSidebarByName('receiver_first_name')}>
            <AddressBookIcon aria-hidden='true' />
            {t('contacts')}
          </button>
        )}
      </div>
      <Formik
        initialValues={{
          ...initValues,
        }}
        validateOnMount
        validationSchema={validationSchema}
        innerRef={innerRef}
        onSubmit={() => {}}>
        {({ handleSubmit, values }) => {
          const visibilitySchema = prepareVisibilitySchema(
            viewBlock?.elements,
            values
          );

          const isPassport = values['pass_type'] === 'passport';

          const visibleInputs = inputElements?.filter(
            (elem) => visibilitySchema[elem?.key ?? '']
          );

          return (
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}>
              <FormInputsContainer $isPassportLayout={isPassport}>
                {visibleInputs}
              </FormInputsContainer>
              <FormikEffects
                triggerSubmit={isNextButtonClicked}
                visibilitySchema={visibilitySchema}
                inputDateFormFieldNames={inputDateFormFieldNames}
                setGlobalStateFormValues={setGlobalStateFormValues}
                setGlobalStateFormIsValid={setGlobalStateFormIsValid}
              />
            </form>
          );
        }}
      </Formik>
    </ParcelParticipantDetailsContainer>
  );
};

export default observer(ParcelParticipantPersonalFormDynamic);
